<div class="custom-component-wrapper" [style]="wrapperStyle()">
  <div
    class="custom-component-container"
    [style]="containerStyle()"
    [class.has-fixed-width]="hasFixedWidth()"
    [class.has-border]="hasBorder()"
  >
    <!-- Component preview -->
    <div class="custom-component-preview" [class.has-fixed-height]="height() !== null && height() !== undefined">
      @if (!resolvedComponent()) {
        <div class="custom-component-placeholder">
          @if (componentId()) {
            <span class="custom-component-error">Component not found: {{ componentId() }}</span>
          } @else {
            <span>No component selected. Click the pencil to configure.</span>
          }
        </div>
      }
      <ng-container #componentHost></ng-container>
    </div>

    <!-- Edit button (pencil icon, shown on hover) -->
    @if (editor().isEditable) {
      <button class="edit-btn" (click)="openSettings()" title="Edit custom component settings">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
        </svg>
      </button>
    }

    <!-- Resize handles -->
    @if (editor().isEditable) {
      <div class="resize-handle resize-handle-right" (mousedown)="onWidthResizeStart($event)"></div>
      <div class="resize-handle resize-handle-bottom" (mousedown)="onHeightResizeStart($event)"></div>
    }
  </div>
</div>

<!-- Settings modal -->
<ion-modal
  cssClass="custom-component-settings-modal"
  [isOpen]="showSettings()"
  [backdropDismiss]="true"
  (didDismiss)="closeSettings()"
>
  <ng-template>
    <ion-header>
      <ion-toolbar>
        <ion-title>Custom Component Settings</ion-title>
      </ion-toolbar>
    </ion-header>

    <ion-content>
      <div class="settings-content">
        <!-- LEFT: Monaco editor -->
        <div class="settings-left">
          <div class="panel-header">
            <span class="panel-title">Component Config</span>
            @if (availablePresets().length) {
              <span class="presets-badge">Presets: {{ availablePresets().join(', ') }}</span>
            }
          </div>
          <div class="config-editor-container">
            <dojo-json-editor dojoCustomComponentJsonEditor [formCtrl]="configJsonCtrl" />
          </div>
        </div>

        <!-- RIGHT: Layout & Appearance -->
        <div class="settings-right">
          <!-- Layout section -->
          <div class="settings-section">
            <h4 class="settings-section-title">Layout</h4>

            <dojo-number-input
              label="Width (px)"
              size="xs"
              [formCtrl]="widthCtrl"
              placeholder="Auto"
              [min]="100"
              (blur)="applyWidth()"
            />
            <dojo-number-input
              label="Height (px)"
              size="xs"
              [formCtrl]="heightCtrl"
              placeholder="Auto"
              [min]="50"
              (blur)="applyHeight()"
            />

            <dojo-number-input
              label="Padding (px)"
              size="xs"
              [formCtrl]="paddingCtrl"
              placeholder="0"
              [min]="0"
              [step]="4"
              (blur)="applyPadding()"
            />

            <!-- Alignment -->
            @if (hasFixedWidth()) {
              <div class="settings-row">
                <label class="settings-label">Alignment</label>
                <mat-button-toggle-group
                  class="custom-toggle-group size-xs"
                  hideSingleSelectionIndicator
                  [value]="align() || 'left'"
                  (change)="setAlign($event.value)"
                >
                  <mat-button-toggle value="left">
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                    >
                      <line x1="17" y1="10" x2="3" y2="10" />
                      <line x1="21" y1="6" x2="3" y2="6" />
                      <line x1="21" y1="14" x2="3" y2="14" />
                      <line x1="17" y1="18" x2="3" y2="18" />
                    </svg>
                    Left
                  </mat-button-toggle>
                  <mat-button-toggle value="center">
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                    >
                      <line x1="18" y1="10" x2="6" y2="10" />
                      <line x1="21" y1="6" x2="3" y2="6" />
                      <line x1="21" y1="14" x2="3" y2="14" />
                      <line x1="18" y1="18" x2="6" y2="18" />
                    </svg>
                    Center
                  </mat-button-toggle>
                  <mat-button-toggle value="right">
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                    >
                      <line x1="21" y1="10" x2="7" y2="10" />
                      <line x1="21" y1="6" x2="3" y2="6" />
                      <line x1="21" y1="14" x2="3" y2="14" />
                      <line x1="21" y1="18" x2="7" y2="18" />
                    </svg>
                    Right
                  </mat-button-toggle>
                </mat-button-toggle-group>
              </div>
            }
          </div>

          <div class="section-divider"></div>

          <!-- Appearance section -->
          <div class="settings-section">
            <h4 class="settings-section-title">Appearance</h4>

            <div class="settings-row toggle-row">
              <span class="settings-label">Show border</span>
              <ion-toggle [checked]="hasBorder()" (ionChange)="toggleBorder($any($event).detail.checked)" />
            </div>
          </div>
        </div>
      </div>
    </ion-content>

    <ion-footer class="settings-footer">
      <div class="settings-footer-inner">
        <dojo-button fill="outline" (click)="closeSettings()">Done</dojo-button>
      </div>
    </ion-footer>
  </ng-template>
</ion-modal>
